import * as CubeTree from '../../cubeTree/index';
import * as UIToolkit from '../../UIToolkit/index';
import FrontCubeData from '../../frontCubeData/index';
import Config from '../../config/index';
import Utils from '../../utils/util';
import Select from '../select/index';

const moveStep: number = Config.get({
    moduleName: 'base',
    key: 'moveStep'
});

// 查找选中组件中，可以移动的组件
function findAllSelectedCanMove(): Array<CubeId> {
    let result: Set<CubeId> = new Set();
    let selected: Array<CubeId> = Select.getAll();

    // 找层板
    selected.forEach((cubeId: CubeId): void => {
        if (CubeTree.isPartition(cubeId) && !CubeTree.isCabinet(cubeId)) {
            result.add(cubeId);
        }
    });

    // 找商品、文本框、隔板
    selected.forEach((cubeId: CubeId): void => {
        if (CubeTree.isGoods(cubeId)
            || CubeTree.isBafflePlate(cubeId)
            || CubeTree.isTextarea(cubeId)) {
            let itsLayer: CubeId|null = CubeTree.findCubeLayer(cubeId);

            if (!result.has(itsLayer)) {
                result.add(cubeId);
            }
        }
    });

    return Array.from(result);
}

function makeMoveTaskQueue(cubeIds: Array<CubeId>, direction: string): MoveTask {
    let result: MoveTask = {
        physics: [],
        logic: []
    };

    // 收集坐标移动任务
    collectPositionMoveTask(cubeIds, result.physics, direction);

    // 收集位置移动任务
    collectTransMoveTask(cubeIds, result.logic, direction);

    return result;
}

function collectPositionMoveTask(cubeIds: Array<CubeId>, positionMove: Array<PhysicsMoveTask>, direction: string): void {
    cubeIds.forEach((cubeId: CubeId): void => {
        let posValue: CubePosValue = CubeTree.getCubePosValue(cubeId);
        let from: CubePosValue = Utils.deepCopy(posValue);

        if (CubeTree.isInLayer(cubeId)) {
            let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);

            // 非手动放置层板, 并且移动cube不是隔板，禁止坐标修改
            if (UIToolkit.layoutType(itsLayer) !== '|-|--|---' && !CubeTree.isBafflePlate(cubeId)) {
                return;
            }

            // 手动放置层板中禁止上下坐标移动
            if (direction === 'up' || direction === 'down') {
                return;
            }
        }

        switch (direction) {
            case 'up':
                posValue.posY += stepCompute(cubeId);
                break;

            case 'down':
                posValue.posY -= stepCompute(cubeId);
                break;

            case 'left':
                posValue.posX -= stepXCompute(cubeId, 'left');
                break;

            case 'right':
                posValue.posX += stepXCompute(cubeId, 'right');
                break;

            default:
                break;
        }

        // 如果移动对象是货架中的层板，并且货架设置了打孔值，则需要做y值修正
        if (CubeTree.isPartition(cubeId) && CubeTree.isInShelf(cubeId)) {
            let itsShelf: CubeId = CubeTree.findCubeShelf(cubeId);

            posValue.posY = UIToolkit.layerYRectify(itsShelf, posValue.posY);
        }

        positionMove.push({
            from: {
                moveTarget: cubeId,
                position: {
                    left: from.posX,
                    bottom: from.posY
                }
            },
            to: {
                moveTarget: cubeId,
                position: {
                    left: posValue.posX,
                    bottom: posValue.posY
                }
            }
        });
    });
}

function stepXCompute(cubeId: CubeId, dir: string): PhysicsMeasurement {
    if (!CubeTree.isBafflePlate(cubeId)) {
        return moveStep;
    }

    if (!CubeTree.isInLayer(cubeId)) {
        return moveStep;
    }

    let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);
    let posValue: CubePosValue = CubeTree.getCubePosValue(cubeId);
    let moduleWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(itsLayer, 'moduleWidth');
    let bafflePlateSize: PhysicsSize = UIToolkit.layerCubeSize(cubeId);

    if (dir === 'left' && posValue.posX > bafflePlateSize.width) {
        return moveStep;
    }

    if (dir === 'right' && posValue.posX < moduleWidth - bafflePlateSize.width) {
        return moveStep;
    }

    return 0;
}

function stepCompute(cubeId: CubeId): PhysicsMeasurement {
    if (!CubeTree.isPartition(cubeId)) {
        return moveStep;
    }

    if (!CubeTree.isInShelf(cubeId)) {
        return moveStep;
    }

    let shelf: CubeId = CubeTree.findCubeShelf(cubeId);
    let holeSpacing: PhysicsMeasurement = FrontCubeData.getNumberValue(shelf, 'holeSpacing');

    if (holeSpacing <= 0) {
        return moveStep;
    }

    return holeSpacing;
}

// 暂时只对商品、文本框做位置快捷移动支持
function collectTransMoveTask(cubeIds: Array<CubeId>, transMove: Array<LogicMoveTask>, direction: string): void {
    cubeIds.forEach((cubeId: CubeId): void => {
        if (!CubeTree.isGoods(cubeId)
            && !CubeTree.isTextarea(cubeId)) {
            return;
        }

        // 没在层板上
        if (!CubeTree.isInLayer(cubeId)) {
            return;
        }

        if (CubeTree.isInLayer(cubeId)) {
            let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);

            // 非手动放置层板中的商品，禁止左右穿梭
            if (UIToolkit.layoutType(itsLayer) === '|-|--|---'
                && (direction === 'left' || direction === 'right')) {
                return;
            }
        }

        switch (direction) {
            case 'up':
                makeCubeTransUpTask(transMove, cubeId, cubeIds);
                break;

            case 'down':
                makeCubeTransDownTask(transMove, cubeId, cubeIds);
                break;

            case 'left':
                makeCubeTransLeftTask(transMove, cubeId, cubeIds);
                break;

            case 'right':
                makeCubeTransRightTask(transMove, cubeId, cubeIds);
                break;

            default:
                break;
        }
    });
}

// 向上跨层板穿梭
function makeCubeTransUpTask(transMove: Array<LogicMoveTask>, cubeId: CubeId, allMove: Array<CubeId>): void {
    let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);

    // 画布中的层板，禁止层板之间穿梭
    if (CubeTree.isInStage(itsLayer)) {
        return;
    }

    let targetLayer: CubeId|null = CubeTree.findNextLayer(itsLayer);

    // 越界
    if (!targetLayer || CubeTree.isRoof(targetLayer)) {
        return;
    }

    let moveCubeIndex: number = CubeTree.getCubeIndex(cubeId);
    let targetCube: CubeId|null = CubeTree.findChildCubeByIndexSkip(targetLayer, moveCubeIndex);
    let prevBefore: CubeId|null = CubeTree.findNextCube(cubeId, allMove);

    transMove.push({
        from: {
            parent: itsLayer,
            moveTarget: cubeId,
            before: prevBefore
        },
        to: {
            parent: targetLayer,
            moveTarget: cubeId,
            insertBefore: targetCube
        }
    });
}

// 向下跨层板穿梭
function makeCubeTransDownTask(transMove: Array<LogicMoveTask>, cubeId: CubeId, allMove: Array<CubeId>): void {
    let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);

    // 画布中的层板，禁止层板之间穿梭
    if (CubeTree.isInStage(itsLayer)) {
        return;
    }

    let targetLayer: CubeId|null = CubeTree.findPrevLayer(itsLayer);

    // 越界
    if (!targetLayer || CubeTree.isRoof(targetLayer)) {
        return;
    }

    let moveCubeIndex: number = CubeTree.getCubeIndex(cubeId);
    let targetCube: CubeId|null = CubeTree.findChildCubeByIndexSkip(targetLayer, moveCubeIndex);
    let prevBefore: CubeId|null = CubeTree.findNextCube(cubeId, allMove);

    transMove.push({
        from: {
            parent: itsLayer,
            moveTarget: cubeId,
            before: prevBefore
        },
        to: {
            parent: targetLayer,
            moveTarget: cubeId,
            insertBefore: targetCube
        }
    });
}

function makeCubeTransLeftTask(transMove: Array<LogicMoveTask>, cubeId: CubeId, allMove: Array<CubeId>): void {
    let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);
    let moveCubes: CubeId[] = allMove.filter((cubeId: CubeId): boolean => {
        return !CubeTree.isBafflePlate(cubeId);
    });
    let targetCube: CubeId|null = CubeTree.findPrevCube(cubeId, moveCubes);

    // 左侧越界, 或者碰到障碍物
    if (!targetCube || CubeTree.isBafflePlate(targetCube)) {
        return;
    }

    let prevBefore: CubeId|null = CubeTree.findNextCube(cubeId, moveCubes);

    transMove.push({
        from: {
            parent: itsLayer,
            moveTarget: cubeId,
            before: prevBefore
        },
        to: {
            parent: itsLayer,
            moveTarget: cubeId,
            insertBefore: targetCube
        }
    });
}

function makeCubeTransRightTask(transMove: Array<LogicMoveTask>, cubeId: CubeId, allMove: Array<CubeId>): void {
    let itsLayer: CubeId = CubeTree.findCubeLayer(cubeId);
    let moveCubes: CubeId[] = allMove.filter((cubeId: CubeId): boolean => {
        return !CubeTree.isBafflePlate(cubeId);
    });
    let nextCube: CubeId|null = CubeTree.findNextCube(cubeId, moveCubes);

    if (!Utils.isExist(nextCube) || CubeTree.isBafflePlate(nextCube)) {
        return;
    }

    let targetCube: CubeId|null = CubeTree.findNextCube(nextCube);

    transMove.push({
        from: {
            parent: itsLayer,
            moveTarget: cubeId,
            before: nextCube
        },
        to: {
            parent: itsLayer,
            moveTarget: cubeId,
            insertBefore: targetCube
        }
    });
}

export {
    findAllSelectedCanMove,
    makeMoveTaskQueue
};
